export interface ApprovalBusinessDisplayTarget {
  operationNames?: string[]
  businessType?: string
  businessId?: string
  businessName?: string
}

export interface ApprovalRequestDetailRow {
  label: string
  value: string
}

const NAME_ONLY_BUSINESS_TYPES = new Set(['ITEM_ORDER', 'TABLE_RECORD'])
const MASKED_APPROVAL_VALUE = '[已隐藏]'
const ITEM_REQUEST_ID_KEYS = new Set(['businessid', 'itemorderid', 'orderitemid', 'itemid', 'goodsid', 'productid', 'skuid'])
const TABLE_REQUEST_ID_KEYS = new Set(['businessid', 'tablerecordid', 'tableid', 'deskid'])

export function formatApprovalBusinessName(
  target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId' | 'businessName'>,
): string {
  const businessName = normalizeText(target?.businessName)
  if (businessName) {
    return businessName
  }

  const businessType = normalizeText(target?.businessType)
  if (businessType && NAME_ONLY_BUSINESS_TYPES.has(businessType)) {
    return ''
  }

  return normalizeText(target?.businessId)
}

export function formatApprovalTarget(target: ApprovalBusinessDisplayTarget, fallback = '敏感操作审核'): string {
  const operation = target.operationNames?.map(normalizeText).filter(Boolean).join('、')
  const business = formatApprovalBusinessName(target)
  return [operation, business].filter(Boolean).join(' - ') || fallback
}

export function formatApprovalRequestPath(
  method: string | undefined,
  path: string | undefined,
  target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>,
): string {
  const requestLine = [normalizeText(method) || '--', normalizeText(path)].filter(Boolean).join(' ')
  return redactApprovalRequestText(requestLine || '--', target)
}

export function formatApprovalRequestPayload(
  value: string | undefined,
  target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>,
): string {
  if (!value) {
    return '--'
  }
  try {
    const parsed = JSON.parse(value)
    const redacted = redactApprovalRequestValue(parsed, target)
    return JSON.stringify(redacted, null, 2)
  } catch (_error) {
    return redactApprovalRequestText(value, target)
  }
}

export function buildApprovalRequestDetailRows({
  approvalDetailRows,
  requestBody,
  requestQuery,
}: {
  approvalDetailRows?: ApprovalRequestDetailRow[]
  requestBody?: string
  requestQuery?: string
}): ApprovalRequestDetailRow[] {
  const persistedRows = normalizeApprovalDetailRows((approvalDetailRows || []).map(row => detailRow(row.label, row.value)))
  if (persistedRows.length) {
    return persistedRows
  }

  const body = parseApprovalRequestValue(requestBody)
  const query = parseApprovalQueryValue(requestQuery)
  const itemOrder = isRecord(body.itemOrder) ? body.itemOrder : {}
  const discountInfo = isRecord(body.discountInfo) ? body.discountInfo : {}
  const discountList = Array.isArray(body.discountList) ? body.discountList.filter(isRecord) : []
  const count = normalizeNumber(itemOrder.count)
  const salePrice = normalizeNumber(itemOrder.salePrice)
  const itemAmount = count !== undefined && salePrice !== undefined ? count * salePrice : undefined
  const totalDiscountAmount = discountList.reduce((total, item) => {
    const amount = normalizeNumber(item.discountAmount)
    return amount === undefined ? total : total + Math.abs(amount)
  }, 0)
  return normalizeApprovalDetailRows([
    detailRow('商品', itemOrder.itemName || itemOrder.name || body.itemName || body.productName),
    count !== undefined ? detailRow('数量', `${count}`) : null,
    itemAmount !== undefined ? detailRow('涉及金额', formatAmountFromCent(itemAmount)) : null,
    ...buildDiscountRows(discountInfo),
    totalDiscountAmount ? detailRow('优惠金额', formatAmountFromCent(totalDiscountAmount)) : null,
    ...discountList.flatMap(buildDiscountRows),
    detailRow('取消原因', body.remarks || body.cancelReason || query.cancelReason),
    detailRow('申请原因', body.reason || body.applicationReason || body.applyReason),
    detailRow('备注', body.remark || body.note),
  ])
}

function redactApprovalRequestValue(
  value: unknown,
  target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>,
  key?: string,
): unknown {
  if (!shouldRedactApprovalTarget(target)) {
    return value
  }
  if (key && shouldRedactRequestKey(key, target?.businessType)) {
    return MASKED_APPROVAL_VALUE
  }
  if (Array.isArray(value)) {
    return value.map(item => redactApprovalRequestValue(item, target))
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        redactApprovalRequestValue(entryValue, target, entryKey),
      ]),
    )
  }
  if (matchesBusinessId(value, target?.businessId)) {
    return MASKED_APPROVAL_VALUE
  }
  return value
}

function redactApprovalRequestText(
  value: string,
  target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>,
): string {
  if (!shouldRedactApprovalTarget(target)) {
    return value
  }
  if (looksLikeQueryString(value)) {
    return value.split('&').map(part => redactQueryPart(part, target)).join('&')
  }
  const businessId = normalizeText(target?.businessId)
  return businessId ? value.split(businessId).join(MASKED_APPROVAL_VALUE) : value
}

function redactQueryPart(part: string, target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>): string {
  const separatorIndex = part.indexOf('=')
  if (separatorIndex < 0) {
    return redactApprovalRequestText(part, target)
  }
  const key = part.slice(0, separatorIndex)
  const value = part.slice(separatorIndex + 1)
  if (shouldRedactRequestKey(decodeURIComponentSafely(key), target?.businessType) || matchesBusinessId(decodeURIComponentSafely(value), target?.businessId)) {
    return `${key}=${MASKED_APPROVAL_VALUE}`
  }
  return part
}

function looksLikeQueryString(value: string): boolean {
  return value.includes('=') && !value.trimStart().startsWith('{') && !value.trimStart().startsWith('[')
}

function shouldRedactApprovalTarget(target?: Pick<ApprovalBusinessDisplayTarget, 'businessType' | 'businessId'>): boolean {
  return NAME_ONLY_BUSINESS_TYPES.has(normalizeText(target?.businessType))
}

function shouldRedactRequestKey(key: string, businessType?: string): boolean {
  const normalizedKey = key.replace(/[-_\s]/g, '').toLowerCase()
  if (normalizeText(businessType) === 'ITEM_ORDER') {
    return ITEM_REQUEST_ID_KEYS.has(normalizedKey) || isTableIdentifierKey(normalizedKey)
  }
  if (normalizeText(businessType) === 'TABLE_RECORD') {
    return isTableIdentifierKey(normalizedKey)
  }
  return false
}

function isTableIdentifierKey(normalizedKey: string): boolean {
  return TABLE_REQUEST_ID_KEYS.has(normalizedKey) || ((normalizedKey.includes('table') || normalizedKey.includes('desk')) && normalizedKey.endsWith('id'))
}

function matchesBusinessId(value: unknown, businessId?: string): boolean {
  const normalizedBusinessId = normalizeText(businessId)
  return Boolean(normalizedBusinessId && normalizeText(value as string | number | null | undefined) === normalizedBusinessId)
}

function decodeURIComponentSafely(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch (_error) {
    return value
  }
}

function normalizeText(value?: number | string | null): string {
  if (value === undefined || value === null) {
    return ''
  }
  return String(value).trim()
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function parseApprovalRequestValue(value?: string): Record<string, any> {
  if (!value) return {}
  try {
    const parsed = JSON.parse(value)
    return isRecord(parsed) ? parsed : {}
  } catch (_error) {
    return {}
  }
}

function parseApprovalQueryValue(value?: string): Record<string, string> {
  if (!value) return {}
  const normalized = value.startsWith('?') ? value.slice(1) : value
  return normalized.split('&').reduce<Record<string, string>>((result, part) => {
    if (!part) return result
    const separatorIndex = part.indexOf('=')
    const rawKey = separatorIndex >= 0 ? part.slice(0, separatorIndex) : part
    const rawValue = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : ''
    const key = decodeURIComponentSafely(rawKey)
    if (!key) return result
    result[key] = decodeURIComponentSafely(rawValue)
    return result
  }, {})
}

function normalizeNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : undefined
}

function formatAmountFromCent(value: unknown): string {
  const normalized = normalizeNumber(value)
  if (normalized === undefined) return normalizeText(value as string | number | null | undefined)
  return `¥${(normalized / 100).toFixed(2)}`
}

function formatDiscountRateText(value: unknown): string {
  const rate = normalizeNumber(value)
  if (rate === undefined || rate <= 0) return ''
  const displayedRate = rate <= 1 ? rate * 10 : (rate <= 10 ? rate : rate / 10)
  return `${Number(displayedRate.toFixed(2))}折`
}

function detailRow(label: string, value: unknown): ApprovalRequestDetailRow | null {
  const normalizedValue = normalizeText(value as string | number | null | undefined)
  return normalizedValue ? { label, value: normalizedValue } : null
}

function buildDiscountRows(discountInfo: Record<string, any> = {}) {
  const discountType = normalizeNumber(discountInfo.discountType)
  const discountAmount = normalizeNumber(discountInfo.discountAmount)
  const discountRate = normalizeNumber(discountInfo.discountRate)
  const isRateDiscount = discountType === 1 || discountType === 41
  const isWaiver = discountType === 2 || discountType === 42 || discountType === 11
  return [
    isRateDiscount && discountRate !== undefined
      ? detailRow('折扣率', formatDiscountRateText(discountRate))
      : null,
    isWaiver && discountAmount !== undefined
      ? detailRow(discountType === 11 ? '抹零金额' : '减免金额', formatAmountFromCent(Math.abs(discountAmount)))
      : null,
    discountAmount !== undefined ? detailRow('优惠金额', formatAmountFromCent(Math.abs(discountAmount))) : null,
    detailRow('申请原因', discountInfo.discountDescription || discountInfo.discountReason),
  ]
}

function normalizeApprovalDetailRows(rows: Array<ApprovalRequestDetailRow | null>): ApprovalRequestDetailRow[] {
  const seen = new Set<string>()
  return rows
    .filter((row): row is ApprovalRequestDetailRow => Boolean(row))
    .filter(row => row.value && row.value !== '--')
    .filter(row => {
      const key = `${row.label}:${row.value}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}
